import type { ReactNode } from 'react';
import { cx } from './cx';

export interface C1StatusRow {
  id: string;
  /** Leading glyph. Status is never colour alone — an icon and a word carry it too. */
  icon?: ReactNode;
  label: ReactNode;
  value: ReactNode;
  tone?: 'good' | 'warning' | 'critical' | 'neutral';
  /**
   * External destination. When set, the whole row is a link — used so a bursar can
   * open the EduCore screen that acts on the exception, not merely read it.
   */
  href?: string;
}

interface C1StatusListProps {
  items: C1StatusRow[];
  /** Caps the height and scrolls inside the card, so the page never grows unbounded. */
  scroll?: boolean;
  className?: string;
}

/** Name-and-state rows: integration health, pupils at risk, overdue items. */
export function C1StatusList({ items, scroll = false, className }: C1StatusListProps) {
  return (
    <ul className={cx('c1-statusList', scroll && 'c1-statusList--scroll', className)}>
      {items.map((item) => {
        const body = (
          <>
            {item.icon ? (
              <span className="c1-statusIcon" aria-hidden="true">
                {item.icon}
              </span>
            ) : null}
            <span className="c1-statusName">{item.label}</span>
            <span
              className={cx(
                'c1-statusValue',
                item.tone && item.tone !== 'neutral' && `c1-statusValue--${item.tone}`,
              )}
            >
              {item.value}
            </span>
          </>
        );

        return (
          <li key={item.id} className={cx('c1-statusRow', item.href && 'c1-statusRow--link')}>
            {item.href ? (
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="c1-statusRowLink"
              >
                {body}
              </a>
            ) : (
              body
            )}
          </li>
        );
      })}
    </ul>
  );
}
